<template>
  <div>
    <div align="left"></div>
    <br />
    <div class="text-left">
      <h5 class="font-weight-bold text-primary">Disbursement Time Setting</h5>

      <div class="setting-card mt-3" style="max-width: 540px;">
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Start Time"
              label-for="start_time"
              label-class="control-label font-weight-bold required"
              style="font-size: 12px"
            >
              <input
                v-model="form.start_time"
                v-validate="{ required: true, regex: /^([01]\d|2[0-3]):[0-5]\d$/ }"
                type="time"
                name="start_time"
                id="start_time"
                data-vv-as="Start Time"
                class="form-control"
              />
              <p class="field-error">{{ veeErrors.first("start_time") }}</p>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="End Time"
              label-for="end_time"
              label-class="control-label font-weight-bold required"
              style="font-size: 12px"
            >
              <input
                v-model="form.end_time"
                v-validate="{ required: true, regex: /^([01]\d|2[0-3]):[0-5]\d$/ }"
                type="time"
                name="end_time"
                id="end_time"
                data-vv-as="End Time"
                class="form-control"
              />
              <p class="field-error">
                {{ veeErrors.first("end_time") || endTimeRangeError }}
              </p>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-button
              variant="primary"
              block
              size="lg"
              :disabled="saveDisabled"
              @click="save"
            >
              <b-spinner v-if="saving" small></b-spinner>
              <span v-else>SAVE</span>
            </b-button>
          </b-col>
        </b-row>
        <b-row v-if="setting.updated_by_username" class="mt-2">
          <b-col>
            <p class="last-updated mb-0">
              <em>
                Last updated by {{ latestData.updated_by_username || setting.updated_by_username }}.
                <span v-if="latestData.updated_at">{{ latestData.updated_at | DateTimeV4 }}</span>
                <span v-else>—</span>
              </em>
            </p>
          </b-col>
        </b-row>
      </div>

      <b-row class="mt-4">
        <b-col md="2" class="my-1">
          <b-form-group label-cols-sm="6" label="Show" class="mb-0">
            <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <br />
      <b-table
        responsive
        show-empty
        ref="disbursementTimeLogsTable"
        :items="getDataLogs"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        head-variant="light"
        class="bg-white"
      >
        <div slot="table-busy" class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
        <template v-slot:cell(updated_by)="{ item }">
          <p class="mb-0">{{ item.updated_by }}</p>
        </template>
        <template v-slot:cell(updated_at)="{ item }">
          <p class="mb-0">
            <span v-if="item.updated_at">{{ item.updated_at | DateTimeV4 }}</span>
            <span v-else>—</span>
          </p>
        </template>
        <template v-slot:cell(changed_from)="{ item }">
          <p class="mb-0">{{ formatRange(item.changed_from) }}</p>
        </template>
        <template v-slot:cell(changed_to)="{ item }">
          <p class="mb-0">{{ formatRange(item.changed_to) }}</p>
        </template>
      </b-table>
      <b-row class="mx-auto">
        <b-col md="4" align="left" class="mx-auto px-0">
          <div v-if="totalRows == 0 || totalRows == null">
            Showing 0 Records
          </div>
          <div v-else>
            Showing {{ fromPage[currentPage - 1] }} to
            {{ toPage[currentPage - 1] }} of {{ totalRows }} Records
          </div>
        </b-col>
        <b-col md="4" class="mx-auto"></b-col>
        <b-col md="4" align="right" class="px-0">
          <b-pagination
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      form: {
        start_time: '',
        end_time: '',
      },
      setting: {
        start_time: '',
        end_time: '',
        updated_by_username: '',
        updated_at: '',
      },
      saving: false,
      endTimeRangeError: '',
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      totalRows: 0,
      latestData: {},
      fromPage: [],
      toPage: [],
      fields: [
        { key: 'updated_by', label: 'Updated By', class: 'text-left' },
        { key: 'updated_at', label: 'Updated At', class: 'text-left' },
        { key: 'changed_from', label: 'Changed From', class: 'text-left' },
        { key: 'changed_to', label: 'Changed To', class: 'text-left' },
      ],
    };
  },
  computed: {
    formDirty() {
      return (
        this.form.start_time !== this.setting.start_time ||
        this.form.end_time !== this.setting.end_time
      );
    },
    endAfterStart() {
      if (!this.form.start_time || !this.form.end_time) return true;
      return this.toMinutes(this.form.end_time) > this.toMinutes(this.form.start_time);
    },
    saveDisabled() {
      return (
        this.saving ||
        !this.form.start_time ||
        !this.form.end_time ||
        !this.formDirty ||
        this.veeErrors.any()
      );
    },
  },
  watch: {
    'form.start_time'() { this.endTimeRangeError = ''; },
    'form.end_time'() { this.endTimeRangeError = ''; },
  },
  mounted() {
    this.loadSetting();
  },
  methods: {
    toMinutes(hhmm) {
      const [h, m] = hhmm.split(':').map(Number);
      return h * 60 + m;
    },
    formatRange(range) {
      if (!range) return '—';
      return `${range.start_time} | ${range.end_time}`;
    },
    loadSetting() {
      this.$store
        .dispatch('ClaimSettingDisbursementTimeModule/getDisbursementTime')
        .then((response) => {
          const data = response.data.data || {};
          this.setting = { ...this.setting, ...data };
          this.form.start_time = data.start_time || '08:30';
          this.form.end_time = data.end_time || '22:30';
        })
        .catch(() => {
          this.form.start_time = '08:30';
          this.form.end_time = '22:30';
        });
    },
    getDataLogs(ctx, callback) {
      const vm = this;
      const payload = { page: ctx.currentPage, limit: ctx.perPage };
      this.$store
        .dispatch('ClaimSettingDisbursementTimeModule/getDisbursementTimeLogs', payload)
        .then((response) => {
          vm.totalRows = response.data.data.total_record;
          const from = [];
          const to = [];
          let sum = 0;
          for (let i = 1; i <= vm.currentPage; i += 1) {
            sum = i * vm.perPage;
            if (vm.totalRows <= sum) {
              from.push(sum - vm.perPage + 1);
              to.push(vm.totalRows - sum + sum);
            } else {
              from.push(sum - vm.perPage + 1);
              to.push(sum);
            }
          }
          vm.fromPage = from;
          vm.toPage = to;
          // BE returns snapshot rows sorted newest-first: each row is the
          // value at that change moment. Derive "changed_from → changed_to"
          // pairs client-side by pairing row i with row i+1 (older). Last
          // row on the page has no next row available → changed_from null.
          const results = response.data.data.results || [];
          const rows = results.map((row, i) => ({
            id: row.id,
            updated_by: row.changed_by_username,
            updated_at: row.changed_at_utc,
            changed_from: results[i + 1]
              ? { start_time: results[i + 1].start_time, end_time: results[i + 1].end_time }
              : null,
            changed_to: { start_time: row.start_time, end_time: row.end_time },
          }));
          if (ctx.currentPage === 1) {
            vm.latestData = rows[0] || {};
          }
          callback(rows);
        })
        .catch((error) => {
          this.$swal({
            type: 'error',
            title: 'Error!',
            text: error.response ? error.response.data.message : 'Failed get logs',
            showConfirmButton: false,
            timer: 1500,
          });
          callback([]);
        });
      return null;
    },
    save() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        if (!this.endAfterStart) {
          this.endTimeRangeError = 'End Time harus lebih besar dari Start Time';
          return;
        }
        this.saving = true;
        const payload = {
          start_time: this.form.start_time,
          end_time: this.form.end_time,
        };
        this.$store
          .dispatch('ClaimSettingDisbursementTimeModule/updateDisbursementTime', payload)
          .then(() => {
            // BE returns { data: [] } on upsert success — nothing to merge.
            // Re-fetch to refresh setting.updated_by_username + updated_at.
            this.saving = false;
            this.loadSetting();
            this.$swal({
              type: 'success',
              title: 'Your work has been saved',
              showConfirmButton: false,
              scrollbarPadding: false,
              timer: 1500,
            });
            if (this.$refs.disbursementTimeLogsTable) {
              this.$refs.disbursementTimeLogsTable.refresh();
            }
          })
          .catch((error) => {
            this.saving = false;
            // BE 400 shape: { data: ["<msg>"] } — array of one string.
            let text = 'Failed to save';
            if (error.response && error.response.data) {
              const d = error.response.data.data;
              if (Array.isArray(d) && d.length) text = d[0];
              else if (error.response.data.message) text = error.response.data.message;
            }
            this.$swal({
              type: 'error',
              title: 'Oops...',
              text,
              showConfirmButton: false,
              scrollbarPadding: false,
              timer: 1500,
            });
          });
      });
    },
  },
};
</script>

<style scoped>
.setting-card {
  background-color: #ffffff;
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
  padding: 1.25rem 1.5rem;
}
.field-error {
  width: 100%;
  font-size: 80%;
  color: #dc3545;
  margin-bottom: 0;
}
.last-updated {
  font-size: 12px;
  color: #858796;
}
</style>
